import {
 HttpEvent,
 HttpInterceptor,
 HttpHandler,
 HttpRequest,
 HttpResponse,
 HttpErrorResponse,
 HttpHeaders
} from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { retry, catchError, tap } from 'rxjs/operators';

import { UserService } from 'src/app/services/user.service';
import { ApiConnectivityService } from 'src/app/services/api-connectivity.service';
import { NavController } from '@ionic/angular';
import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { HTTP } from '@awesome-cordova-plugins/http/ngx';
import { environment } from '../environments/environment';
import { AppVersion } from '@awesome-cordova-plugins/app-version/ngx';
import { getApiHostFromUrl } from './services/api-connectivity.util';

type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'head' | 'delete' | 'upload' | 'download';

/**
 * The kiln status API answers with a bare "Invalid authentication" whenever its own call
 * to the KilnLink token endpoint comes back as anything other than 200, so the message
 * cannot distinguish a dead session from a momentary backend failure. Signing out on the
 * first one throws people with perfectly good tokens back to the login screen, so wait
 * for the failures to both repeat and persist. An expired token is reported separately
 * and is not subject to this.
 */
const AMBIGUOUS_AUTH_FAILURE_THRESHOLD = 3;
const AMBIGUOUS_AUTH_PERSISTENCE_MS = 20000;

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
 private ambiguousAuthFailures = 0;
 private firstAmbiguousAuthFailureAt = 0;

 constructor(
   public userService: UserService,
   public navCtrl: NavController,
   private nativeHttp: HTTP,
   private platform: Platform,
   private appVersion: AppVersion,
   private apiConnectivity: ApiConnectivityService
 ) {}

intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
   if (!this.platform.is('cordova')) {
     let token = this.userService.getAuthToken();
     let headers = {
       'Content-Type': 'application/json',
       'Accept': 'application/json'
     }

     if(request.url.includes(environment.kilnApi)) {
       headers["x-app-info"] = `${environment.package_name},${environment.clientId}`;
       if(token) {
         // headers["Authorization"] = `Bearer ${token}`;
         headers["x-app-auth"] = "token";
         headers["x-access-token"] = token;
       }
     }

     let authenticatedRequest = request.clone({setHeaders: headers})
     return this.pipeWithOptionalRetry(request, next.handle(authenticatedRequest));
   } else {
     return this.pipeWithOptionalRetry(request, from(this.handleNativeRequest(request)));
   }
 }

 /** Auth calls hit Cognito twice server-side; retrying 4xx doubles that wait on failures. */
 private shouldRetryRequest(url: string): boolean {
   return !url.includes('/users/login') && !url.includes('/password/update');
 }

 private pipeWithOptionalRetry(request: HttpRequest<any>, source: Observable<HttpEvent<any>>): Observable<HttpEvent<any>> {
   const afterErrorHandling = (src: Observable<HttpEvent<any>>) =>
     src.pipe(
       tap((event) => this.recordConnectivitySuccess(request.url, event)),
       catchError((error) => {
         this.apiConnectivity.recordFailure(request.url, error);
         return this.handleError(error, this.userService);
       }),
     );
   if (this.shouldRetryRequest(request.url)) {
     return afterErrorHandling(source.pipe(retry(1)));
   }
   return afterErrorHandling(source);
 }

 private recordConnectivitySuccess(url: string, event: HttpEvent<any>): void {
   if (event instanceof HttpResponse && event.status >= 200 && event.status < 400) {
     this.apiConnectivity.recordSuccess(url);
     // Any answered API call proves the session is still good.
     if (getApiHostFromUrl(url)) {
       this.ambiguousAuthFailures = 0;
     }
   }
 }

 /**
  * Counts an ambiguous auth rejection and reports whether the session should end. A burst
  * of parallel requests failing at the same instant is one backend event, not a run of
  * them, so the streak also has to span time before it counts as a dead session.
  */
 private shouldLogOutForAmbiguousAuthFailure(): boolean {
   const now = Date.now();
   if (this.ambiguousAuthFailures === 0) {
     this.firstAmbiguousAuthFailureAt = now;
   }
   this.ambiguousAuthFailures++;

   return this.ambiguousAuthFailures >= AMBIGUOUS_AUTH_FAILURE_THRESHOLD &&
     (now - this.firstAmbiguousAuthFailureAt) >= AMBIGUOUS_AUTH_PERSISTENCE_MS;
 }

 private async addHeaders(request) {
   let token = this.userService.getAuthToken();
   request.headers = request.headers.append("x-app-info", this.appToken());
   // request.headers = request.headers.append("Authorization", `Bearer ${token}`);
   request.headers = request.headers.append("x-access-token", token);
   request.headers = request.headers.append("x-app-auth", "token");
 }

 private async appToken() {
   let name;
   if(!this.platform.is('cordova')) {
     name = environment.package_name;
   } else {
     name = await this.appVersion.getPackageName();
   }

   return `${name},${environment.clientId}`;
 }

 handleError(error: HttpErrorResponse, userService) {
   let error_info = error && error.error;
   let is_token_error = error_info && error_info.hasOwnProperty("error_type") && error_info["error_type"] && error_info["error_type"] === "TokenExpiredError";
   let is_invalid_auth = error_info && (error_info.message === "Invalid Token" || error_info.message === 'Invalid authentication');

   if (is_invalid_auth && !is_token_error && !this.shouldLogOutForAmbiguousAuthFailure()) {
     console.log(`Ignoring auth rejection ${this.ambiguousAuthFailures} on ${error && error.url}, keeping session`);
     return throwError(error);
   }

   if (is_token_error || is_invalid_auth) {
     this.ambiguousAuthFailures = 0;
     if(this.userService) {
       this.userService.tryLogOut();
     } else {
       userService && userService.tryLogOut();
     }
     console.log(error);
     this.navCtrl.navigateForward('/');
   }

   return throwError(error);
 }

 private parseNativeErrorBody(raw: unknown): unknown {
   if (raw == null) {
     return { message: 'Request failed' };
   }
   if (typeof raw !== 'string') {
     return raw;
   }
   const trimmed = raw.trim();
   if (!trimmed) {
     return { message: 'Request failed' };
   }
   try {
     return JSON.parse(trimmed);
   } catch {
     if (trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html') || trimmed.startsWith('<')) {
       return { message: 'Service unavailable' };
     }
     return { message: trimmed.slice(0, 500) };
   }
 }

 private rejectNativeHttpError(error: Record<string, unknown>): Promise<never> {
   return Promise.reject(new HttpErrorResponse({
     error: this.parseNativeErrorBody(error['error']),
     status: typeof error['status'] === 'number' ? error['status'] : 0,
     statusText: typeof error['statusText'] === 'string' ? error['statusText'] : 'Error',
     url: typeof error['url'] === 'string' ? error['url'] : undefined,
   }));
 }

 private async handleNativeRequest(request: HttpRequest<any>): Promise<HttpResponse<any>> {
   let token = this.userService.getAuthToken();
   let app_token = await this.appToken();
   const headerKeys = request.headers.keys();
   const headers = {
     "x-app-info": app_token
   };

   if(token) {
     // headers["Authorization"] = `Bearer ${token}`;
     headers["x-app-auth"] = token;
   }

   headerKeys.forEach((key) => {
     headers[key] = request.headers.get(key);
   });

   try {
     await this.platform.ready();

     const method = <HttpMethod> request.method.toLowerCase();
     const nativeHttpResponse = await this.nativeHttp.sendRequest(request.url, {
       method: method,
       data: request.body,
       headers: headers,
       serializer: 'json',
     });

     let body;
     try {
       body = JSON.parse(nativeHttpResponse.data);
     } catch (error) {
       body = { response: nativeHttpResponse.data };
     }

     const response = new HttpResponse({
       body: body,
       status: nativeHttpResponse.status,
       headers: new HttpHeaders(nativeHttpResponse.headers),
       url: nativeHttpResponse.url,
     });

     // console.log(`returning from http request, body: ${JSON.stringify(body)}`)
     return Promise.resolve(response);
   } catch (error) {
     if (!error?.status) { return Promise.reject(error); }
     return this.rejectNativeHttpError(error as Record<string, unknown>);
   }
 }
}
